/**
 * Creative Studio — AI Creative Director.
 *
 * Step 1: InputStep      — single input + optional product/reference uploads
 * Step 2: DirectionsStep — creative brief + 3 visual direction cards
 * Step 3: AssetStep      — full campaign asset kit gallery + save-to-campaign
 *
 * Inheritance: Active Weekly Strategy campaign is auto-pulled on mount so the
 * brief inherits objective/audience/positioning without re-entry.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import DashboardLayout from '../components/DashboardLayout';
import InputStep      from '../components/CreativeStudio/InputStep';
import DirectionsStep from '../components/CreativeStudio/DirectionsStep';
import AssetStep      from '../components/CreativeStudio/AssetStep';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreativeStudioPage() {
  const [step, setStep] = useState('input');  // input | directions | assets
  const [prompt, setPrompt] = useState('');
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [project, setProject] = useState(null);
  const [inheritedStrategy, setInheritedStrategy] = useState(null);
  const [selectedDir, setSelectedDir] = useState(null);

  // Pre-fetch active campaign so the inheritance banner shows on first paint.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/strategy/current`);
        const s = data?.strategy;
        if (alive && s && s.status === 'launched' && s.campaign_id) {
          setInheritedStrategy({
            id: s.id,
            campaign_name: s.campaign_name,
            campaign_objective: s.campaign_objective,
          });
        }
      } catch { /* silent — banner is best-effort */ }
    })();
    return () => { alive = false; };
  }, []);

  const reset = () => {
    setStep('input');
    setPrompt('');
    setUploads([]);
    setProject(null);
    setSelectedDir(null);
  };

  const runBrief = useCallback(async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    try {
      const assetIds = uploads.filter(u => u.asset_id).map(u => u.asset_id);
      const { data } = await axios.post(`${API}/api/creative-studio/brief`, {
        prompt,
        upload_asset_ids: assetIds,
      });
      setProject({ id: data.project_id, brief: data.brief, directions: data.directions });
      // server-side wins (its inheritance lookup is the source of truth)
      if (data.inherited_strategy) setInheritedStrategy(data.inherited_strategy);
      setStep('directions');
    } catch {
      toast.error('Could not direct the creative.');
    } finally {
      setLoading(false);
    }
  }, [prompt, uploads]);

  const pickDirection = useCallback(async (directionId) => {
    if (!project?.id) return;
    setSelectedDir(directionId);
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/creative-studio/generate`, {
        project_id: project.id,
        direction_id: directionId,
      });
      setProject((p) => ({ ...p, ...(data.project || {}) }));
      setStep('assets');
    } catch {
      toast.error('Could not generate the campaign.');
      setSelectedDir(null);
    } finally {
      setGenerating(false);
    }
  }, [project]);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="creative-studio-page">
        <AnimatePresence mode="wait">
          {step === 'input' && (
            <InputStep
              key="input"
              value={prompt}
              setValue={setPrompt}
              uploads={uploads}
              setUploads={setUploads}
              onRun={runBrief}
              loading={loading}
              inheritedStrategy={inheritedStrategy}
            />
          )}
          {step === 'directions' && project && (
            <DirectionsStep
              key="directions"
              brief={project.brief}
              directions={project.directions}
              inheritedStrategy={inheritedStrategy}
              prompt={prompt}
              onPick={pickDirection}
              generating={generating}
              selectedId={selectedDir}
              onReset={reset}
            />
          )}
          {step === 'assets' && project && (
            <AssetStep key="assets" project={project} onReset={reset} />
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
